var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Checkbox, FormControlLabel, IconButton, Link as MuiLink, Stack, TextField, Tooltip, Typography, } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationType } from '../../../enum';
import { RumEntityLabel, rumEntityName } from '../../../observability/rumEntity';
import { formatNumberByLocale } from '../../../util';
import { AddressPlacesAutocomplete, Autocomplete, ColumnFilterHeader, ConfirmDialog, DataGrid, ErrorMessageBox, FallbackComponent, NumberInput, UnControlledFormField, useClientTableFilter, } from '../../common';
import { useCountryAPI } from '../../manageUsers/selectors';
import { MAX_TEXT_LENGTH } from '../constants';
import { OrgConfigWizardContext, useOrgConfigDirtyRegister } from '../context';
import { useCreateLocationAPI, useDeleteLocationAPI, useLocationsAPI } from '../selectors';
import { LocationEditModal } from './LocationEditModal';
import { resolveTotalSeats, useLocationFormState } from './useLocationFormState';
var AddLocationPanel = styled(Box)(function (_a) {
    var theme = _a.theme;
    return ({
        padding: theme.spacing(6),
        backgroundColor: theme.palette.primary.light,
        borderRadius: theme.shape.borderRadius,
    });
});
var LocationLink = styled(MuiLink)({
    textAlign: 'left',
    textDecoration: 'underline',
    cursor: 'pointer',
});
export var LocationsStep = function () {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var apiKey = (_a = process.env.REACT_APP_GOOGLE_PLACES_API_KEY) !== null && _a !== void 0 ? _a : '';
    var t = useTranslation('orgConfig', { keyPrefix: 'orgConfigWizard.locationsStep' }).t;
    var _j = useContext(OrgConfigWizardContext), configVersionId = _j.configVersionId, multiGeoEnabled = _j.multiGeoEnabled, versionLoading = _j.versionLoading, versionError = _j.versionError;
    var _k = useLocationsAPI(configVersionId), locationsData = _k[0], fetchLocations = _k[1];
    var _l = useCreateLocationAPI(), createData = _l[0], createLocation = _l[1], clearCreateData = _l[2];
    var _m = useState(null), deleteId = _m[0], setDeleteId = _m[1];
    var _o = useDeleteLocationAPI(deleteId !== null && deleteId !== void 0 ? deleteId : 0), deleteData = _o[0], deleteLocation = _o[1], clearDeleteData = _o[2];
    var _p = useState(null), editLocationId = _p[0], setEditLocationId = _p[1];
    var form = useLocationFormState();
    var address = form.address, setAddress = form.setAddress, locationName = form.locationName, setLocationName = form.setLocationName, locationType = form.locationType, setLocationType = form.setLocationType, unlimitedSeats = form.unlimitedSeats, setUnlimitedSeats = form.setUnlimitedSeats, totalSeats = form.totalSeats, setTotalSeats = form.setTotalSeats, city = form.city, setCity = form.setCity, stateProvince = form.stateProvince, setStateProvince = form.setStateProvince, stateProvinceCode = form.stateProvinceCode, setStateProvinceCode = form.setStateProvinceCode, countryName = form.countryName, setCountryName = form.setCountryName, countryCode = form.countryCode, setCountryCode = form.setCountryCode, addressCountryResolved = form.addressCountryResolved, setAddressCountryResolved = form.setAddressCountryResolved, latitude = form.latitude, setLatitude = form.setLatitude, longitude = form.longitude, setLongitude = form.setLongitude, resetAddressFields = form.resetAddressFields;
    var _q = useCountryAPI(), countries = _q[0], fetchCountries = _q[1];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var locations = (_c = (_b = locationsData.data) === null || _b === void 0 ? void 0 : _b.locations) !== null && _c !== void 0 ? _c : [];
    var multiGeo = (_e = (_d = locationsData.data) === null || _d === void 0 ? void 0 : _d.multiGeoEnabled) !== null && _e !== void 0 ? _e : multiGeoEnabled;
    var canAddMore = multiGeo || locations.length < 1;
    // Hide Country when Google Places resolves it to prevent manual override of API data; show as fallback when API is unavailable or address does not resolve.
    var showCountryField = !apiKey || !addressCountryResolved;
    useEffect(function () {
        fetchCountries();
    }, [fetchCountries]);
    var initialLocationsRef = useRef(null);
    useEffect(function () {
        var _a;
        if (((_a = locationsData.data) === null || _a === void 0 ? void 0 : _a.locations) && !initialLocationsRef.current) {
            initialLocationsRef.current = locationsData.data.locations;
        }
    }, [locationsData.data]);
    var checkIsDirty = useCallback(function () {
        if (!initialLocationsRef.current)
            return false;
        return JSON.stringify(locations) !== JSON.stringify(initialLocationsRef.current);
    }, [locations]);
    useOrgConfigDirtyRegister(checkIsDirty);
    useEffect(function () {
        if (configVersionId) {
            fetchLocations();
        }
    }, [configVersionId, fetchLocations]);
    useEffect(function () {
        if (createData.data) {
            fetchLocations();
            clearCreateData();
            resetAddressFields();
        }
        return function () {
            clearCreateData();
        };
    }, [createData.data, fetchLocations, configVersionId, clearCreateData, resetAddressFields]);
    useEffect(function () {
        var isDeleteSuccess = deleteData.statusCode != null && deleteData.statusCode >= 200 && deleteData.statusCode < 300;
        if (isDeleteSuccess) {
            fetchLocations();
            setDeleteId(null);
            clearDeleteData();
        }
    }, [deleteData.statusCode, fetchLocations, configVersionId, clearDeleteData]);
    var handleAddressChange = useCallback(function (value, components) {
        var _a, _b, _c, _d, _e;
        setAddress(value);
        if (components.city)
            setCity(components.city);
        if (components.stateProvince)
            setStateProvince((_a = components.stateProvince) !== null && _a !== void 0 ? _a : '');
        setStateProvinceCode((_b = components.stateProvinceCode) !== null && _b !== void 0 ? _b : null);
        if (components.countryName) {
            setCountryName(components.countryName);
            setCountryCode((_c = components.countryCode) !== null && _c !== void 0 ? _c : null);
            setAddressCountryResolved(true);
        }
        else {
            setCountryName('');
            setCountryCode(null);
            setAddressCountryResolved(false);
        }
        setLatitude((_d = components.latitude) !== null && _d !== void 0 ? _d : null);
        setLongitude((_e = components.longitude) !== null && _e !== void 0 ? _e : null);
    }, [
        setAddress,
        setCity,
        setCountryName,
        setCountryCode,
        setAddressCountryResolved,
        setLatitude,
        setLongitude,
        setStateProvince,
        setStateProvinceCode,
    ]);
    var handleAddLocation = function () {
        if (!configVersionId)
            return;
        createLocation(__assign(__assign({ configVersionId: configVersionId, locationName: locationName === null || locationName === void 0 ? void 0 : locationName.trim(), address: address || null, city: city || null, stateProvince: stateProvince || null, stateProvinceCode: stateProvinceCode !== null && stateProvinceCode !== void 0 ? stateProvinceCode : null, countryName: countryName || 'Unknown', countryCode: countryCode !== null && countryCode !== void 0 ? countryCode : null }, (latitude != null && longitude != null ? { latitude: latitude, longitude: longitude } : {})), { totalSeats: resolveTotalSeats(unlimitedSeats, totalSeats), locationType: locationType }));
    };
    var handleDeleteConfirm = function () {
        if (deleteId) {
            deleteLocation();
        }
    };
    var filterColumns = useMemo(function () { return [
        { field: 'locationName', filter: 'text', sortable: true },
        {
            field: 'locationType',
            filter: 'enum',
            sortable: true,
            getLabel: function (value) { return (value === 'in_house' ? t('locationTypeInHouse') : t('locationTypeOutsourced')); },
        },
        { field: 'region', filter: 'text', sortable: true },
        { field: 'countryName', filter: 'enum', sortable: true },
        { field: 'stateProvince', filter: 'text', sortable: true },
        { field: 'city', filter: 'text', sortable: true },
        { field: 'totalSeats', sortable: true },
    ]; }, [t]);
    var _r = useClientTableFilter({
        rows: locations,
        columns: filterColumns,
    }), processedRows = _r.processedRows, getFilterState = _r.getFilterState, getAllValues = _r.getAllValues, applyFilter = _r.applyFilter;
    var makeFilterHeader = useCallback(function (field, label) { return function () { return (_jsx(ColumnFilterHeader, { label: label, filterState: getFilterState(field), allValues: getAllValues(field), onApply: function (update) { return applyFilter(field, update); } })); }; }, [applyFilter, getAllValues, getFilterState]);
    var columns = useMemo(function () { return [
        {
            field: 'locationName',
            headerName: t('siteLocationName'),
            flex: 2,
            sortable: false,
            renderHeader: makeFilterHeader('locationName', t('siteLocationName')),
            renderCell: function (params) { return (_jsx(LocationLink, __assign({ component: "button", onClick: function () { return setEditLocationId(params.row.id); }, "data-dd-action-name": "Edit location: ".concat(rumEntityName('location', params.row.id)) }, { children: _jsx(RumEntityLabel, __assign({ kind: "location", id: params.row.id, hasActionName: false }, { children: params.value })) }))); },
        },
        {
            field: 'locationType',
            headerName: t('locationType'),
            flex: 1,
            sortable: false,
            renderHeader: makeFilterHeader('locationType', t('locationType')),
            valueFormatter: function (v) { return (v.value === 'in_house' ? t('locationTypeInHouse') : t('locationTypeOutsourced')); },
        },
        {
            field: 'region',
            headerName: t('region'),
            flex: 1,
            sortable: false,
            renderHeader: makeFilterHeader('region', t('region')),
        },
        {
            field: 'countryName',
            headerName: t('country'),
            flex: 1,
            sortable: false,
            renderHeader: makeFilterHeader('countryName', t('country')),
        },
        {
            field: 'stateProvince',
            headerName: t('stateProvince'),
            flex: 1,
            sortable: false,
            renderHeader: makeFilterHeader('stateProvince', t('stateProvince')),
        },
        {
            field: 'city',
            headerName: t('city'),
            flex: 1,
            sortable: false,
            renderHeader: makeFilterHeader('city', t('city')),
        },
        {
            field: 'totalSeats',
            headerName: t('numberOfSeats'),
            flex: 1,
            sortable: false,
            renderHeader: makeFilterHeader('totalSeats', t('numberOfSeats')),
            valueFormatter: function (v) { return (v.value == null ? t('unlimited') : formatNumberByLocale(v.value)); },
        },
        {
            field: 'id',
            headerName: '',
            flex: 0.25,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: function (params) { return (_jsx(IconButton, __assign({ size: "small", onClick: function () { return setDeleteId(params.row.id); }, "aria-label": "delete", sx: {
                    color: function (theme) { return theme.palette.text.primary; },
                    '&:hover': {
                        color: function (theme) { return theme.palette.text.primary; },
                        backgroundColor: function (theme) { return theme.palette.action.hover; },
                    },
                } }, { children: _jsx(FontAwesomeIcon, { icon: "trash-can" }) }))); },
        },
    ]; }, [t, makeFilterHeader]);
    if (versionLoading) {
        return _jsx(FallbackComponent, { requesting: true, overlay: true });
    }
    if (versionError) {
        return (_jsx(Typography, __assign({ color: "error" }, { children: (_f = versionError === null || versionError === void 0 ? void 0 : versionError.message) !== null && _f !== void 0 ? _f : t('versionError', { defaultValue: 'Failed to load configuration' }) })));
    }
    if (!configVersionId) {
        return _jsx(FallbackComponent, { requesting: true, overlay: true });
    }
    return (_jsxs(_Fragment, { children: [_jsxs(Stack, __assign({ spacing: 3 }, { children: [_jsx(AddLocationPanel, { children: _jsxs(Stack, __assign({ direction: { xs: 'column', md: 'row' }, spacing: 2.5, flexWrap: "wrap", useFlexGap: true, alignItems: { md: 'flex-end' } }, { children: [_jsx(UnControlledFormField, __assign({ label: t('address'), sx: { flex: 1, minWidth: 200 } }, { children: _jsx(AddressPlacesAutocomplete, { apiKey: apiKey, value: address, onChange: handleAddressChange, fullWidth: true, placeholder: t('enterAddress') }) })), showCountryField && (_jsx(UnControlledFormField, __assign({ label: t('country'), required: true, sx: { flex: 1, minWidth: 150 } }, { children: _jsx(Autocomplete, { fullWidth: true, options: countries.data, placeholder: t('selectCountry'), loading: countries.requesting, value: countryName, getOptionValue: function (o) { var _a; return (_a = o === null || o === void 0 ? void 0 : o.name) !== null && _a !== void 0 ? _a : ''; }, onChange: function (value) { var _a; return setCountryName((_a = value) !== null && _a !== void 0 ? _a : ''); } }) }))), _jsx(UnControlledFormField, __assign({ label: t('siteLocationName'), required: true, sx: { flex: 1, minWidth: 200 } }, { children: _jsx(TextField, { fullWidth: true, value: locationName, onChange: function (e) { return setLocationName(e.target.value); }, placeholder: t('siteLocationNamePlaceholder'), inputProps: { maxLength: MAX_TEXT_LENGTH } }) })), _jsx(UnControlledFormField, __assign({ label: t('locationType'), required: true, sx: { flex: 1, minWidth: 130 } }, { children: _jsx(Autocomplete, { options: [
                                            { value: LocationType.InHouse, label: t('locationTypeInHouse') },
                                            { value: LocationType.Outsourced, label: t('locationTypeOutsourced') },
                                        ], getOptionValue: function (o) { return o.value; }, getOptionLabel: function (o) { return o.label; }, value: locationType, onChange: function (value) { return setLocationType(value); } }) })), _jsx(UnControlledFormField, __assign({ label: t('unlimitedSeats'), sx: {
                                        flexShrink: 0,
                                        '& .MuiInputLabel-root': { visibility: 'hidden' },
                                    } }, { children: _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: unlimitedSeats, onChange: function (e) {
                                                var checked = e.target.checked;
                                                setUnlimitedSeats(checked);
                                                setTotalSeats(checked ? '' : 1);
                                            } }), label: t('unlimitedSeats') }) })), _jsx(UnControlledFormField, __assign({ label: t('numberOfSeats'), sx: { width: 100 } }, { children: _jsx(NumberInput, { fullWidth: true, min: 1, max: 9999999, value: totalSeats === '' ? null : Number(totalSeats), onChange: function (v) { return setTotalSeats(v == null ? '' : Number(v)); }, disabled: unlimitedSeats }) })), _jsx(UnControlledFormField, __assign({ label: t('numberOfSeats'), sx: {
                                        width: 'auto',
                                        flexShrink: 0,
                                        '& .MuiInputLabel-root': {
                                            visibility: 'hidden',
                                        },
                                    } }, { children: _jsx(Tooltip, __assign({ title: canAddMore ? '' : t('upgradeToMultiGeoTooltip') }, { children: _jsx(Box, __assign({ component: "span", sx: { display: 'inline-flex' } }, { children: _jsx(Button, __assign({ variant: "outlined", onClick: handleAddLocation, disabled: (totalSeats === '' && !unlimitedSeats) ||
                                                    !canAddMore ||
                                                    !!createData.requesting ||
                                                    !(locationName === null || locationName === void 0 ? void 0 : locationName.trim()) ||
                                                    !countryName }, { children: t('addLocationButton') })) })) })) }))] })) }), _jsx(ErrorMessageBox, { errors: [createData.error] }), _jsx(FallbackComponent, __assign({ requesting: !!locationsData.requesting, overlay: true }, { children: _jsx(DataGrid, { rows: processedRows, columns: columns, getRowId: function (row) { return row.id; }, autoHeight: true, pageSize: 10, rowHeight: 40 }) }))] })), _jsx(LocationEditModal, { open: !!editLocationId, onClose: function () { return setEditLocationId(null); }, locationId: editLocationId, onSaved: function () { return fetchLocations(); } }), _jsx(ConfirmDialog, { open: !!deleteId, onClose: function () { return setDeleteId(null); }, title: t('deleteConfirmTitle'), message: deleteId
                    ? t('deleteConfirmMessage', {
                        locationName: (_h = (_g = locations.find(function (l) { return l.id === deleteId; })) === null || _g === void 0 ? void 0 : _g.locationName) !== null && _h !== void 0 ? _h : '',
                    })
                    : '', onConfirm: handleDeleteConfirm })] }));
};
